import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, History } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';

interface AuditoriaLogPanelProps {
  rateioId: string;
  ugId: string;
}

type LogEntry = {
  id: string;
  entidade: string;
  entidade_id: string;
  campo: string;
  valor_anterior: string | null;
  valor_novo: string | null;
  contexto: Record<string, any> | null;
  alterado_em: string;
};

const CAMPO_LABEL: Record<string, string> = {
  percent_rateio: 'Percentual',
  status: 'Status',
  status_aprovacao: 'Aprovação da UG',
  situacao: 'Status da UC',
};

const formatValor = (v: string | null) => (v === null ? '—' : v);
const formatData = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Log de Auditoria (Seção 3 da spec): "toda mudança de percentual ou qualquer
 * alteração no rateio é registrada... log disponível para todos os perfis,
 * sem restrição." Mostra os eventos deste rateio (itens/status) + da UG.
 */
const AuditoriaLogPanel: React.FC<AuditoriaLogPanelProps> = ({ rateioId, ugId }) => {
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<LogEntry[]>([]);

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setLoading(true);
    Promise.all([
      supabase
        .from('rateio_auditoria_log')
        .select('*')
        .eq('entidade', 'rateio_ug')
        .eq('entidade_id', ugId)
        .order('alterado_em', { ascending: false }),
      supabase
        .from('rateio_auditoria_log')
        .select('*')
        .in('entidade', ['rateio', 'rateio_item'])
        .eq('contexto->>rateio_id', rateioId)
        .order('alterado_em', { ascending: false }),
    ])
      .then(([ugRes, rateioRes]) => {
        if (cancelado) return;
        if (ugRes.error) throw ugRes.error;
        if (rateioRes.error) throw rateioRes.error;
        const merged = [...(ugRes.data || []), ...(rateioRes.data || [])] as LogEntry[];
        merged.sort((a, b) => new Date(b.alterado_em).getTime() - new Date(a.alterado_em).getTime());
        setEntries(merged);
      })
      .catch(error => console.error('Error loading auditoria log', error))
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [aberto, rateioId, ugId]);

  return (
    <div className="border-t border-gray-100 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setAberto(o => !o)}
        className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/40"
      >
        <span className="flex items-center gap-1.5">
          <History size={14} /> Log de Alterações
        </span>
        {aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {aberto && (
        <div className="px-6 pb-4">
          {loading ? (
            <p className="text-xs text-gray-400 py-2">Carregando...</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">Nenhuma alteração registrada ainda.</p>
          ) : (
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5 max-h-64 overflow-y-auto">
              {entries.map(e => (
                <li key={e.id} className="flex items-start justify-between gap-3 border-b border-gray-50 dark:border-gray-800 pb-1.5">
                  <span>
                    <strong className="text-gray-800 dark:text-gray-100">{CAMPO_LABEL[e.campo] || e.campo}</strong>
                    {e.contexto?.uc ? <span className="text-gray-400"> (UC {e.contexto.uc})</span> : null}
                    {': '}
                    {formatValor(e.valor_anterior)} → {formatValor(e.valor_novo)}
                  </span>
                  <span className="text-gray-400 whitespace-nowrap">{formatData(e.alterado_em)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditoriaLogPanel;
